import React, { Key, useState } from "react"
import { Button, message } from "antd"
import { useTypedSelector } from "../../../hooks/useTypedSelector"
import { ITargetDataForUpdate, SB_CAMPAIGNS_FILTER_TYPES } from "../types/sbCampaignApi.types"
import { useActions } from "../../../hooks/useActions"
import sbTargetApi from "../api/sbTarget.api"
import sbPlacementApi from "../api/sbPlacement.api"
import { ISbBidAdjustmentItem } from "../types/sbPlacementApi.types"
import { ISbTargetDataForTable, ITargetDataForTable } from "../types/sbTarget.types"
import {
  buildBidAdjustmentItemFromBidding,
  computeDeflatedCPC,
} from "../hooks/helpers/sbPlacementModifier.helper"

export function buildApplySuccessMessage(targetCount: number, adjustmentCount: number): string {
  const parts: string[] = []
  if (targetCount > 0) {
    parts.push(`${targetCount} target bid${targetCount === 1 ? "" : "s"}`)
  }
  if (adjustmentCount > 0) {
    parts.push(`${adjustmentCount} placement adjustment${adjustmentCount === 1 ? "" : "s"}`)
  }
  return `Applied ${parts.join(" and ")} on Amazon`
}

export function buildTargetBidForUpdate(
  limitedCPC: number | null,
  adjustment: ISbBidAdjustmentItem | null | undefined,
  minBid: number
): number {
  return computeDeflatedCPC(limitedCPC, adjustment, minBid) ?? 0
}

function buildTargetRowKey({
  campaignId,
  adGroupId,
  targetId,
}: {
  campaignId: string
  adGroupId: string
  targetId: string
}): string {
  return [campaignId, adGroupId || "", targetId].map(String).join(":")
}

export function collectTargetsForApply({
  targetDataForTable,
  selectedTargetIds,
  pendingAdjustments,
  bidAdjustmentsByCampaign,
  minTargetCPC,
}: {
  targetDataForTable: ISbTargetDataForTable[]
  selectedTargetIds: Key[]
  pendingAdjustments: Record<string, ISbBidAdjustmentItem>
  bidAdjustmentsByCampaign: Record<string, ISbBidAdjustmentItem>
  minTargetCPC: number
}): ITargetDataForUpdate[] {
  const selectedIds = new Set(selectedTargetIds.map(String))
  const campaignsWithPendingAdjustments = new Set(Object.keys(pendingAdjustments).map(String))
  const targetsById = new Map<string, ITargetDataForUpdate>()

  targetDataForTable.forEach((campaign) => {
    const campaignId = String(campaign.campaignId)
    const shouldApplyCampaignTargets = campaignsWithPendingAdjustments.has(campaignId)

    campaign.targetData.forEach((target: ITargetDataForTable) => {
      const rowKey = buildTargetRowKey({
        campaignId,
        adGroupId: String(target.adGroupId || ""),
        targetId: String(target.targetId),
      })
      if (!shouldApplyCampaignTargets && !selectedIds.has(rowKey)) {
        return
      }
      if (targetsById.has(rowKey)) {
        return
      }

      targetsById.set(rowKey, {
        campaignId: target.campaignId,
        targetId: target.targetId,
        adGroupId: target.adGroupId,
        oldBid: target.bid,
        bid:
          target.deflatedCPC ??
          buildTargetBidForUpdate(
            target.limitedCPC,
            bidAdjustmentsByCampaign[campaignId],
            minTargetCPC
          ),
        targetName: target.targetName,
      })
    })
  })

  return [...targetsById.values()]
}

interface IApplyOnAmazon {
  selectedTargetIds: Key[]
  onApplied?: () => void
}

const ApplyOnAmazon: React.FC<IApplyOnAmazon> = ({ selectedTargetIds, onApplied }) => {
  const [loading, setLoading] = useState<boolean>(false)

  const { targetDataForTable } = useTypedSelector((select) => select.sponsoredBrandsTargetForTable)
  const { pendingAdjustments, placementData } = useTypedSelector(
    (select) => select.sponsoredBrandsPlacements
  )
  const minTargetCPC = useTypedSelector(
    (select) => select.sponsoredBrands.campaignData.minTargetCPC
  )
  const { dateRange, isOrder } = useTypedSelector((select) => select.filter)
  const {
    fetchSponsoredBrandsCampaigns,
    resetSponsoredBrandsPlacements,
    resetSponsoredBrandsTargets,
  } = useActions()

  function collectSelectedTargets(): ITargetDataForUpdate[] {
    if (!targetDataForTable) {
      return []
    }

    const bidAdjustmentsByCampaign = placementData.reduce<Record<string, ISbBidAdjustmentItem>>(
      (acc, placement) => {
        const campaignId = String(placement.campaignId)
        acc[campaignId] =
          pendingAdjustments[campaignId] ??
          buildBidAdjustmentItemFromBidding(campaignId, placement.bidding)
        return acc
      },
      { ...pendingAdjustments }
    )

    return collectTargetsForApply({
      targetDataForTable,
      selectedTargetIds,
      pendingAdjustments,
      bidAdjustmentsByCampaign,
      minTargetCPC,
    })
  }

  async function handleOnClick() {
    const selectedTargetForUpdate = collectSelectedTargets()
    const adjustments = Object.values(pendingAdjustments)

    if (!selectedTargetForUpdate.length && !adjustments.length) {
      message.error("Nothing to apply")
      return
    }

    setLoading(true)
    try {
      await Promise.all([
        selectedTargetForUpdate.length
          ? sbTargetApi.updateSbTarget({ targetData: selectedTargetForUpdate })
          : Promise.resolve(),
        adjustments.length
          ? sbPlacementApi.updateBidAdjustments({ adjustments })
          : Promise.resolve(),
      ])

      message.success(buildApplySuccessMessage(selectedTargetForUpdate.length, adjustments.length))
      onApplied?.()
      resetSponsoredBrandsPlacements()
      resetSponsoredBrandsTargets()
      fetchSponsoredBrandsCampaigns({
        fromDate: dateRange.from,
        toDate: dateRange.to,
        isManage: true,
        filterType: isOrder ? SB_CAMPAIGNS_FILTER_TYPES.ORDERS : SB_CAMPAIGNS_FILTER_TYPES.DATE,
      })
    } catch {
      message.error("Failed to apply on Amazon")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Button loading={loading} onClick={handleOnClick}>
      Apply On Amazon
    </Button>
  )
}

export default ApplyOnAmazon
